import { useEffect, useId, useRef, useState } from "react";

type Props = {
  /** Компактная кнопка в шапке доски */
  className?: string;
  /** Скрыть кнопку-триггер (открытие из «Меню») */
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Справка — не сухой список клавиш, а «как это помогает на уроке»: у каждого
 * пункта жирным — что нажать, дальше — зачем. Владелец просила писать
 * живо: справку открывают посреди урока, и она должна читаться за секунды.
 * Обещать только то, что доска делает на самом деле.
 */
type HelpItem = [what: string, why: string];

const SECTIONS: { title: string; items: HelpItem[] }[] = [
  {
    title: "Как двигаться по доске",
    items: [
      ["Колёсико мыши", "приближает прямо к курсору — туда, куда вы смотрите, а не в центр экрана."],
      ["Тачпад", "двумя пальцами двигаете доску, щипком приближаете."],
      ["Телефон и планшет", "один палец двигает объекты, двумя пальцами — саму доску."],
      ["Пробел, правая или средняя кнопка мыши", "на время превращают курсор в «руку»: подвинули доску — и пишете дальше, не меняя инструмент."],
      ["«Рука» (H)", "если удобнее двигать доску левой кнопкой."],
      ["Потерялись?", "кнопка с уголками внизу справа покажет всю доску сразу, а мини-карта рядом — где вы сейчас. Щелчок по карте переносит туда."],
    ],
  },
  {
    title: "Инструменты в одну клавишу",
    items: [
      ["V · H · P · R", "курсор, рука, перо, фигуры."],
      ["L · A · F · T · N · E", "прямая, стрелка, рамка, текст, стикер, ластик."],
      ["D", "у пера переключает вид линии: сплошная, пунктир, точки."],
      ["Свои клавиши", "любую можно переназначить в «Настройках» — под свою руку."],
    ],
  },
  {
    title: "Ровные линии и аккуратные чертежи",
    items: [
      ["Shift + перо", "вместо дрожащей линии — ровный отрезок. Почти горизонтальный или вертикальный встанет точно по клетке: оси координат без линейки."],
      ["G", "выключает это прилипание, когда нужна линия с лёгким наклоном. Ещё раз G — включает обратно."],
      ["Shift у фигур", "квадрат вместо прямоугольника, круг вместо овала. С Alt фигура рисуется от центра."],
      ["Alt при перетаскивании", "объект встаёт ровно по клеткам — схема не «плывёт» на пиксель."],
    ],
  },
  {
    title: "Текст, который приятно читать",
    items: [
      ["Двойной щелчок или Enter", "открывают надпись на правку."],
      ["Выделите слово внутри текста", "— жирный, цвет, размер или маркер лягут только на него, а не на всю надпись."],
      ["«- » или «1. » в начале строки", "превращают её в список. Tab вкладывает пункт, Shift+Tab выносит обратно."],
      ["«Фон»", "заливает надпись мягкой плашкой — так выделяют правило или ответ."],
      ["Ctrl+K или 🔗", "ставит ссылку на выделенные слова. Адрес, вписанный в текст, становится ссылкой сам — щелчок откроет его в новой вкладке."],
      ["Ctrl+Alt+C, потом Ctrl+Alt+V", "переносят оформление с одного объекта на другие: все заголовки одинаковые за пару нажатий."],
    ],
  },
  {
    title: "Правка без нервов",
    items: [
      ["Ctrl+Z и Ctrl+Y", "отменить и вернуть. Смело пробуйте — всё возвращается."],
      ["Ctrl+C · Ctrl+V · Ctrl+D", "копировать, вставить, сделать копию рядом."],
      ["Delete", "убирает выделенное."],
      ["[ и ]", "слой назад и вперёд — если надпись спряталась под фигурой."],
      ["«ком» на панели над объектом", "комментарий прямо у задачи: вопрос ученику или подсказка на потом. Число на синем значке — сколько сообщений."],
      ["Фигура сначала, оформление потом", "поставьте фигуру, а цвет, заливку и пунктир выберите в панели сверху."],
    ],
  },
  {
    title: "Виджеты",
    items: [
      ["Калькулятор", "как в Windows: сначала число, потом функция. arcsin 0,5: наберите 0,5 → «Тригонометрия» → 2ⁿᵈ → sin⁻¹ → «=», выйдет 30. DEG — градусы, RAD — радианы."],
      ["Опрос", "отвечают все сразу, и видно, кто как решил. «Изменить варианты» — впишите свои ответы, до восьми."],
      ["Крупнее для всех", "«Передвинуть» в шапке виджета, потом потяните за угол — цифры и кнопки растут вместе с рамкой."],
    ],
  },
  {
    title: "Фреймы и презентации",
    items: [
      ["«Фреймы» в шапке", "список «страниц» занятия: поиск по названию и переход в одно нажатие."],
      ["Двойной щелчок по рамке", "переименовать её."],
      ["Рамка → «PDF» в панели", "скачать её содержимое — готовый конспект урока."],
      ["Мини-слайды справа от PDF", "переключают страницы, а писать можно прямо на доске рядом."],
      ["Показ на весь экран", "у ведущего панель сворачивается кнопкой «Рисовать на доске» — и можно писать поверх."],
    ],
  },
  {
    title: "Вместе с учеником",
    items: [
      ["«Ссылка» в шапке", "ученик заходит без установки и сразу видит, что вы пишете."],
      ["Зажмите Alt", "лазерная указка: яркая точка видна всем и ничего не оставляет на доске. Вместо «вот тут, нет, правее»."],
      ["Щелчок по участнику в списке", "— и вы смотрите туда же, куда он. Подвинули доску сами — следование выключится."],
      ["«Звонок»", "голос и видео прямо рядом с доской, без лишних окон."],
      ["Ссылка с k= в конце", "храните целиком: без этого хвоста права на правку пропадут."],
      ["«Только просмотр»", "у такого участника «рука»: листать можно, менять — нет. Чтобы ученик писал, дайте ссылку с правом редактора."],
    ],
  },
  {
    title: "Конец урока",
    items: [
      ["«Завершить сессию»", "дождётся, пока всё сохранится, даст подписать доску и оставить пару слов о занятии — и проводит в кабинет."],
      ["«Снимки»", "сохраняют доску целиком. Держите чистую заготовку урока и возвращайте её к следующему ученику."],
    ],
  },
];

export function BoardHelpButton({
  className = "wb-btn wb-btn--ghost",
  hideTrigger = false,
  open: openProp,
  onOpenChange,
}: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? Boolean(openProp) : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!controlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {!hideTrigger ? (
        <button
          type="button"
          className={className}
          onClick={() => setOpen(true)}
          title="Справка по доске"
          aria-haspopup="dialog"
        >
          Справка
        </button>
      ) : null}

      {open ? (
        <div className="wb-board-help" role="presentation">
          <button
            type="button"
            className="wb-board-help__backdrop"
            aria-label="Закрыть справку"
            onClick={() => setOpen(false)}
          />
          <div
            className="wb-board-help__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <header className="wb-board-help__head">
              <h2 id={titleId} className="wb-board-help__title">
                Как устроена доска
              </h2>
              <button
                ref={closeRef}
                type="button"
                className="wb-board-help__close"
                onClick={() => setOpen(false)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </header>
            <div className="wb-board-help__body">
              <p className="wb-board-help__lead">
                Здесь всё, что ускоряет урок. Читать подряд не нужно — загляните в нужный
                раздел. А если хочется попробовать руками, нажмите «Обучение»: пройдёмся по
                доске вместе.
              </p>
              {SECTIONS.map((section) => (
                <section key={section.title} className="wb-board-help__section">
                  <h3 className="wb-board-help__section-title">{section.title}</h3>
                  <ul className="wb-board-help__list">
                    {section.items.map(([what, why]) => (
                      <li key={what}>
                        <b className="wb-board-help__what">{what}</b>{" "}
                        {/* Тире перед пояснением, если оно само им не начинается. */}
                        {why.startsWith("—") ? why : `— ${why}`}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
            <footer className="wb-board-help__foot">
              <button
                type="button"
                className="wb-btn wb-btn--primary wb-btn--sm"
                onClick={() => setOpen(false)}
              >
                Понятно
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
}
